import { useEffect, useState, useMemo, useRef } from "react";
import { Row, message, Spin } from "antd";
import { StepContentSection } from "../components";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import { SuccessStepAssets } from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import { useApplication } from "../api";
import { useSegment } from "@dreambigger/shared/src/hooks";
import useCaptureScreenshot from "../hooks/use-capture-screenshot";
import router from "next/router";
import format from "date-fns/format";
import { isInAdminMode } from "../components/AdminApplicationWrapper";

const spinnerIcon = <Loading3QuartersOutlined spin />;

export default function Success({ flow, step, brand, progress }: StepProps) {
  // State variables for saving and submitting application
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [isSubmissionComplete, setIsSubmissionComplete] = useState(false);

  const segment = useSegment();

  const assets: SuccessStepAssets = step.assets;
  const { slug, type } = step;
  const flowId = flow.id;
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow, router.query]
  );
  const submissionDate = assets.submissionDateFormat
    ? format(new Date(), assets.submissionDateFormat)
    : new Date().toISOString();

  const isAdminMode = isInAdminMode();
  const { screenshotAreaRef, captureScreenshot } = useCaptureScreenshot(
    flow.financialInstitution.id
  );
  const applicationIdRef = useRef<string | null>(null);

  // If the application has an external redirect url, set it to state.
  // We will lose access to the application once it is submitted
  useEffect(() => {
    if (!application) {
      return;
    }
    // Store application ID before submission (application becomes inaccessible after submit)
    applicationIdRef.current = application.id;
    if (application.externalRedirect) {
      setRedirectUrl(application.externalRedirect);
    }
  }, [application]);

  useEffect(() => {
    // Before attempting to update, check that the application is available,
    // hasn't been submitted and a save isn't in progress.
    if (saving || submitted || !application) {
      return;
    }

    // In admin mode, persist the success-step response (without resubmitting)
    // so the backend prunes stale responses now that the applicant has reached
    // the terminal step — mirroring the non-admin prune-at-submit behavior.
    // Re-send the existing success fields (e.g. the original submittedAt) so we
    // neither overwrite the original submission timestamp nor create audit noise.
    if (isAdminMode) {
      const existingSuccess = application.responses?.find(
        (response) => response.slug === slug
      );
      setSaving(true);
      applicationHelper
        .update(
          application.id,
          { slug, type, fields: existingSuccess?.fields ?? {} },
          false
        )
        .then((saved) => {
          if (!saved) {
            return;
          }
          message.success(
            "Your updates have been applied. The application has not been resubmitted"
          );
          setSubmitted(true);
        })
        .finally(() => setSaving(false));
      return;
    }

    // Update and save the application. While "saving" is flagged, a spinner animation will be rendered.
    setSaving(true);
    applicationHelper
      .update(
        application.id,
        {
          slug,
          type,
          fields: {
            submittedAt: submissionDate,
          },
        },
        true
      )
      .then((saved) => {
        //don't move forward if the response is not saved
        if (!saved) {
          return;
        }
        // set submission status and track in segment
        segment.track({
          action: "App State Change",
          label: "Application Submitted",
        });
        setSubmitted(true);

        // Commenting this out. We should not have client specific code blocks.
        // Can be refactored into a generic trigger later if needed.

        /*
        // NATIONAL VOLUNTEER WEEK ONLY - track submission in Google Tag Manager.
        if (
          flow.googleTagManager?.container?.name === "national-volunteer-week"
        ) {
          // Track the "applicationSubmit" event in GTM.
          const gtmEventName = "applicationSubmit";

          // The data we will want to push with the GTM event.
          let dataLayerAttributes = {};

          // If a thrivent member use the name from the "name" slug step.
          const name = applicationHelper.findResponseByApplicationId(
            application.id,
            "name"
          );

          // If a non-thrivent member use the name from the "name-non-member" slug step.
          const nonMemberName = applicationHelper.findResponseByApplicationId(
            application.id,
            "name-non-member"
          );

          const fullName =
            name?.fields["fullName"] || nonMemberName?.fields["fullName"] || "";

          // Push data needed by Referral Rock to the NVW GTM Container.
          dataLayerAttributes = {
            event: gtmEventName,
            onboardingApplicationId: application.id,
            // Will send userId to referral rock as externalIdentifer via GTM script
            userId: application.financialUserId,
            fullName,
          };

          // Push the event data to Google Tag Manager.
          if (dataLayerAttributes) {
            if (process.env.NEXT_PUBLIC_GTM_DEBUG === "true") {
              console.info(
                "Pushing applicationSubmit event to Google Tag Manager."
              );
            }
            gtm.push(dataLayerAttributes);
          }
        }
        */
      })
      .finally(() => setSaving(false));
  }, [saving, application]);

  //once application is submitted trigger the theme reload (and confetti!)
  useEffect(() => {
    if (!submitted) {
      return;
    }
    router.push({
      query: {
        ...router.query,
        flowId,
        redirectUrl,
        stepSlug: slug,
        submission: "complete",
      },
    });
  }, [submitted]);

  // Set submission complete state after mount to avoid hydration mismatch
  useEffect(() => {
    if (router.query.submission === "complete") {
      setIsSubmissionComplete(true);
    }
  }, [router.query.submission]);

  //once theme is reloaded, trigger success message and redirect (if configured)
  useEffect(() => {
    // If submission has gone through, exit and redirect (if redirect is configured). Else, save/update.
    if (!isSubmissionComplete) {
      return;
    }

    // Capture screenshot of the success step if enabled
    if (assets.captureScreenshot && applicationIdRef.current) {
      captureScreenshot(applicationIdRef.current, slug);
    }

    const generateRedirectUrl = () => {
      let url = null;
      if (router.query.redirectUrl) {
        url = Array.isArray(router.query.redirectUrl)
          ? router.query.redirectUrl[0]
          : router.query.redirectUrl;
      } else if (assets.externalRedirect) {
        url = assets.externalRedirect;
      }
      if (!url) {
        return null;
      }
      if (!url.includes("http")) {
        return `https://${url}`;
      }
      return url;
    };

    // If not in admin mode and confetti is not disabled, show a success message.
    if (!isAdminMode && !assets.disableConfetti) {
      message.success("Submission Successful!");
    }

    setTimeout(
      () => {
        // Redirect url is set according to the following priority: (1) If a url was specified for this specific application, (2) if url was specified for the step in step assets, (3) none
        const redirectUrl = generateRedirectUrl();
        //using replace to make it harder to navigate back after application is submitted
        redirectUrl && window.location.replace(redirectUrl);

        //Redirect the user after configured delay or default to 5 seconds.
      },
      assets.externalRedirectDelayInMs ? assets.externalRedirectDelayInMs : 5000
    );
  }, [isSubmissionComplete]);

  return (
    <div ref={screenshotAreaRef}>
      <StepContentSection
        brand={brand}
        stepProgress={!isSubmissionComplete ? 100 : progress}
        title={!isSubmissionComplete ? "Processing..." : assets.title ?? ""}
        subTitle={!isSubmissionComplete ? "" : assets.subTitle ?? ""}
        description={
          !isSubmissionComplete
            ? "Please do not navigate away or close this window."
            : assets.description ?? ""
        }
        assets={assets}
        stepType={type}
        heroImage={assets.heroImage}
        heroText={assets.heroText}
        heroLogo={assets.heroLogo}
      >
        {/* While saving, render a spinner */}
        <Row>{saving && <Spin indicator={spinnerIcon} />}</Row>
      </StepContentSection>
    </div>
  );
}
